import React from 'react';
import { Calendar, MapPin, MessageSquare, Navigation, Phone } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
import type { CrewTimeLabel } from '../crewPortalDisplay';

export type CrewRunSheetJob = {
  title?: string;
  eventDate?: string;
  venue?: string;
  myCallTime?: string | null;
  myTimeLabel?: CrewTimeLabel;
  setupTime?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  clientName?: string;
  companyName?: string;
  phone?: string;
};

export const formatCrewTime = (time?: string | null) => {
  if (!time) return 'Not provided';
  try {
    const [hours, minutes] = time.split(':');
    const hour = Number.parseInt(hours, 10);
    const minute = Number.parseInt(minutes, 10);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return time;
    return `${hour % 12 || 12}:${minute.toString().padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
  } catch {
    return time;
  }
};

const CompactTime = ({ label, value, prominent = false }: { label: string; value?: string | null; prominent?: boolean }) => (
  <div className={prominent ? 'rounded-xl border border-brand-neonPink/30 bg-brand-neonPink/10 px-3 py-2.5' : ''}>
    <p className={`text-[9px] font-black uppercase tracking-wider ${prominent ? 'text-brand-neonPink' : 'text-slate-500'}`}>{label}</p>
    <p className={`mt-0.5 font-black text-white ${prominent ? 'text-lg' : 'text-sm'}`}>{formatCrewTime(value)}</p>
  </div>
);

export function CrewRunSheetPriority({ job, missionContent }: {
  job: CrewRunSheetJob;
  missionContent?: React.ReactNode;
}) {
  const eventDate = job.eventDate && job.eventDate !== 'TBD'
    ? new Date(`${job.eventDate.split('T')[0]}T12:00:00`).toLocaleDateString('en-NZ', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      })
    : 'Date to be confirmed';
  const mapQuery = job.venue?.trim();

  return (
    <div className="space-y-4">
      <GlassCard className="rounded-[22px] border-brand-neonBlue/30 p-3.5 sm:p-4">
        <section aria-labelledby="run-sheet-summary-heading">
          <h3 id="run-sheet-summary-heading" className="sr-only">Event Summary</h3>
          <div className="flex items-start gap-3">
            <Calendar size={16} className="mt-1 shrink-0 text-brand-neonBlue" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-black leading-tight text-white sm:text-lg">{job.title || 'Event'}</p>
              <p className="mt-0.5 text-[11px] font-bold text-brand-neonBlue">{eventDate}</p>
            </div>
          </div>

          <div aria-label="Compact event times" className="mt-3 grid grid-cols-2 gap-2 border-t border-white/5 pt-3">
            <div aria-label="Active Shift Crew call time" className="min-w-0">
              <p className={`text-[9px] font-black uppercase tracking-wider ${job.myCallTime ? 'text-brand-neonBlue' : 'text-amber-300'}`}>
                {job.myTimeLabel || 'Your Call Time'}
              </p>
              <p className="mt-0.5 text-lg font-black text-white">{formatCrewTime(job.myCallTime)}</p>
            </div>
            <CompactTime label="Event Start" value={job.startTime} prominent />
            <CompactTime label="Pack-in" value={job.setupTime} />
            <CompactTime label="Event Finish" value={job.endTime} />
          </div>

          {!job.myCallTime && (
            <p role="status" className="mt-2 text-[11px] font-semibold text-amber-300">
              Call time not confirmed — contact Admin.
            </p>
          )}

          <div className="mt-3 flex items-center gap-2 border-t border-white/5 pt-3">
            <MapPin size={15} className="shrink-0 text-brand-neonPink" />
            <p className="min-w-0 flex-1 truncate text-xs font-semibold text-white">{job.venue || 'Venue not provided'}</p>
            {mapQuery && (
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(mapQuery)}`}
                target="_blank"
                rel="noreferrer"
                className="flex min-h-[34px] shrink-0 items-center rounded-lg bg-slate-800 px-2.5 text-[11px] font-bold text-white hover:bg-slate-700"
              >
                <Navigation size={13} className="mr-1" /> Navigate
              </a>
            )}
          </div>
        </section>
      </GlassCard>

      {missionContent}

      <GlassCard className="rounded-[20px] border-white/10 p-3.5 sm:p-4">
        <section aria-labelledby="on-site-contact-heading" className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p id="on-site-contact-heading" className="text-[9px] font-black uppercase tracking-wider text-slate-500">On-site Contact</p>
            <p className="mt-0.5 truncate text-sm font-bold text-white">{job.clientName || 'Contact unavailable'}</p>
            {job.companyName && <p className="truncate text-[11px] font-medium text-slate-400">{job.companyName}</p>}
          </div>
          {job.phone ? (
            <div className="flex shrink-0 gap-2">
              <a aria-label={`Call ${job.clientName || 'on-site contact'}`} href={`tel:${job.phone}`} className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border border-green-500/20 bg-slate-900 text-green-400 hover:border-green-500/50">
                <Phone size={18} />
              </a>
              <a aria-label={`Text ${job.clientName || 'on-site contact'}`} href={`sms:${job.phone}`} className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border border-brand-neonBlue/20 bg-slate-900 text-brand-neonBlue hover:border-brand-neonBlue/50">
                <MessageSquare size={18} />
              </a>
            </div>
          ) : (
            <p className="shrink-0 text-[11px] italic text-slate-500">No phone available</p>
          )}
        </section>
      </GlassCard>
    </div>
  );
}
